import type { AffineTextAttributes } from '@blocksuite/affine-components/rich-text';
import type { BlockSelection, Command } from '@blocksuite/block-std';

import { assertExists } from '@blocksuite/global/utils';
import { INLINE_ROOT_ATTR, type InlineEditor, type InlineRootElement } from '@blocksuite/inline';

import { FORMAT_BLOCK_SUPPORT_FLAVOURS } from './consts.js';

// for block selection
// @ts-ignore vendored code - command type extensions not visible with node module resolution
export const formatBlockCommand: Command<
  'currentBlockSelections',
  never,
  {
    blockSelections?: BlockSelection[];
    styles: AffineTextAttributes;
    mode?: 'replace' | 'merge';
  }
> = (ctx, next) => {
  const blockSelections = ctx.blockSelections ?? ctx.currentBlockSelections;
  assertExists(
    blockSelections,
    '`blockSelections` is required, you need to pass it in args or use `getBlockSelections` command before adding this command to the pipeline.'
  );

  if ((blockSelections as BlockSelection[]).length === 0) return;

  const styles = ctx.styles;
  const mode = ctx.mode ?? 'merge';

  // @ts-ignore vendored code - command chain extensions not visible with node module resolution
  const success = ctx.std.command
    .chain()
    // @ts-ignore vendored code - command chain extensions not visible with node module resolution
    .getSelectedBlocks({
      blockSelections,
      filter: (el: { model: { flavour: string } }) =>
        FORMAT_BLOCK_SUPPORT_FLAVOURS.includes(
          el.model.flavour as BlockSuite.Flavour
        ),
      types: ['block'],
    })
    .inline((ctx: { selectedBlocks?: Element[] }, next: () => void) => {
      const { selectedBlocks } = ctx;
      assertExists(selectedBlocks);

      const selectedInlineEditors = selectedBlocks.flatMap((el: Element) => {
        const inlineRoot = el.querySelector<
          InlineRootElement<AffineTextAttributes>
        >(`[${INLINE_ROOT_ATTR}]`);
        if (inlineRoot) {
          return inlineRoot.inlineEditor;
        }
        return [];
      });

      selectedInlineEditors.forEach((inlineEditor: InlineEditor<AffineTextAttributes>) => {
        inlineEditor.formatText(
          {
            index: 0,
            length: inlineEditor.yTextLength,
          },
          styles,
          {
            mode,
          }
        );
      });

      next();
    })
    .run();

  if (success) next();
};

declare global {
  namespace BlockSuite {
    interface Commands {
      formatBlock: typeof formatBlockCommand;
    }
  }
}
